import React from "react";
import { Link } from "react-router-dom";
import fightHeroMobile from "../../../assets/images/home/fight-punch-1300x865.webp";
import fightHeroDesktop from "../../../assets/images/home/fight-punch-1800x1198.webp";
import archiveImage from "../../../assets/images/ladies-pose-winner-1200x799-hq.jpg";
import devicesImage from "../../../assets/images/devices-900x600-hq.png";
import { Routes } from "../../../types/constants";
import { buildJoinUrl } from "../../../utils/join";
import styles from "./NzCombatSports.module.scss";

const fightHeroSrcSet = `${fightHeroMobile} 1300w, ${fightHeroDesktop} 1800w`;

const featureItems = [
    {
        title: 'Full local fights',
        copy: 'Watch more than the highlight reel, with full bouts and event footage available on demand.'
    },
    {
        title: 'Highlights and interviews',
        copy: 'Catch the key moments, fighter stories, and scene-building content around each event.'
    },
    {
        title: 'NZ fight archive',
        copy: 'A growing home for local combat sports history, from established names to rising talent.'
    },
    {
        title: 'Search by fighter or event',
        copy: 'Find fights, cards, collections, and local athletes without digging through social feeds.'
    }
];

type SampleItem = {
    title: string;
    copy: string;
    image: string;
    alt: string;
    srcSet?: string;
    sizes?: string;
};

const sampleItems: SampleItem[] = [
    {
        title: 'Event archives',
        copy: 'Browse past shows, lineups, and fight cards from the NZ combat sports scene.',
        image: archiveImage,
        alt: 'Local combat sports winners'
    },
    {
        title: 'Fight action',
        copy: 'Replay boxing, kickboxing, MMA, Muay Thai, interviews, and featured matchups.',
        image: fightHeroMobile,
        alt: 'Combat sports action',
        srcSet: fightHeroSrcSet,
        sizes: '(min-width: 900px) 30vw, 100vw'
    },
    {
        title: 'Watch anywhere',
        copy: 'Watch in your browser on modern phones, tablets, laptops, and desktops.',
        image: devicesImage,
        alt: 'CombatTV on multiple devices'
    }
];

const sportItems = ["Boxing", "MMA", "Kickboxing", "Muay Thai"];

const faqItems = [
    {
        question: 'What sports are included?',
        answer: 'CombatTV covers local combat sports including boxing, MMA, kickboxing, Muay Thai, interviews, highlights, and event archive footage.'
    },
    {
        question: 'How much does membership cost?',
        answer: 'Membership is NZ$12 monthly or NZ$100 annually. Both plans include unlimited catalogue access.'
    },
    {
        question: 'Can I cancel anytime?',
        answer: 'Yes. You can cancel from your billing page at any time, and your access continues until the end of your current paid period.'
    },
    {
        question: 'What devices can I watch on?',
        answer: 'CombatTV is web-based, so you can watch on modern phones, tablets, laptops, and desktops.'
    }
];

/**
 * Static learn-more landing page for home traffic and NZ combat sports ad campaigns.
 */
const NzCombatSports = () => (
    <main className={styles.page}>
        <section className={styles.hero}>
            <img
                src={fightHeroDesktop}
                srcSet={fightHeroSrcSet}
                sizes="100vw"
                alt="NZ combat sports athletes"
                className={styles.heroImage}
                decoding="async"
            />
            <div className={styles.heroOverlay} />
            <div className={styles.heroContent}>
                <span className={styles.heroTag}>CombatTV NZ</span>
                <h1>NZ combat sports in one place.</h1>
                <p>
                    Full fights, highlights, interviews, event cards, and a growing archive built for New Zealand fight fans.
                </p>
                <div className={styles.heroPrice}>NZ$12 monthly or NZ$100 annually. Cancel anytime.</div>
                <div className={styles.heroActions}>
                    <Link
                        to={buildJoinUrl({ source: 'nz_combat_sports' })}
                        className={styles.primaryAction}
                    >
                        Start watching
                    </Link>
                    <Link to={Routes.VIDEOS} className={styles.secondaryAction}>Browse fights</Link>
                </div>
            </div>
        </section>

        <section className={styles.section}>
            <div className={styles.sectionHeader}>
                <h2>A home for the local fight scene</h2>
                <p>Explore local events, fighters, full fights, and an archive that keeps growing.</p>
            </div>
            <div className={styles.featureGrid}>
                {featureItems.map((item) => (
                    <article key={item.title} className={styles.featureItem}>
                        <h3>{item.title}</h3>
                        <p>{item.copy}</p>
                    </article>
                ))}
            </div>
        </section>

        <section className={styles.sampleSection}>
            <div className={styles.sectionHeader}>
                <h2>Watch, search, and revisit the action</h2>
                <p>Use CombatTV as a fight library, an event archive, and a simple way to keep up with local combat sports.</p>
            </div>
            <div className={styles.sampleGrid}>
                {sampleItems.map((item) => (
                    <article key={item.title} className={styles.sampleItem}>
                        <img
                            src={item.image}
                            srcSet={item.srcSet}
                            sizes={item.sizes}
                            alt={item.alt}
                            loading="lazy"
                            decoding="async"
                        />
                        <div>
                            <h3>{item.title}</h3>
                            <p>{item.copy}</p>
                        </div>
                    </article>
                ))}
            </div>
        </section>

        <section className={styles.sceneSection}>
            <div>
                <span className={styles.sceneTag}>Built for the scene</span>
                <h2>Preserving local fight history while making new events easier to follow.</h2>
            </div>
            <p>
                CombatTV is designed for fans who want more than short social clips. Search the library, follow events,
                revisit fighters, and support a platform focused on New Zealand combat sports.
            </p>
            <div className={styles.sportList} aria-label="Combat sports covered">
                {sportItems.map((sport) => (
                    <span key={sport}>{sport}</span>
                ))}
            </div>
            <div className={styles.sceneActions}>
                <Link to={Routes.FIGHT_CARDS} className={styles.secondaryAction}>View fight cards</Link>
                <Link to={Routes.CONTACT} className={styles.textAction}>Contact CombatTV</Link>
            </div>
        </section>

        <section className={styles.faqSection}>
            <div className={styles.sectionHeader}>
                <h2>Common questions</h2>
                <p>Quick answers before you start watching.</p>
            </div>
            <div className={styles.faqList}>
                {faqItems.map((item) => (
                    <details key={item.question} className={styles.faqItem}>
                        <summary>{item.question}</summary>
                        <p>{item.answer}</p>
                    </details>
                ))}
            </div>
        </section>

        <section className={styles.finalCta}>
            <div>
                <h2>Ready for more than the highlights?</h2>
                <p>Choose your plan, create your account, and start watching in one simple checkout.</p>
            </div>
            <div className={styles.finalActions}>
                <Link
                    to={buildJoinUrl({ source: 'nz_combat_sports' })}
                    className={styles.primaryAction}
                >
                    Join CombatTV
                </Link>
                <Link to={Routes.VIDEOS} className={styles.secondaryLightAction}>Browse videos</Link>
            </div>
        </section>
    </main>
);

export default NzCombatSports;
